/**
 * One order, opened from the queue.
 *
 * The same component on all three views: a card on the desk, a shadowed card floating over
 * the map, and a dialog on the timeline. The order itself comes first — where, who, what
 * came of it — and the drivers who could take it last: an order opens by itself the moment
 * it is taken, and the automation usually has a driver on it before anybody would choose
 * one, so what the dispatcher reads is the order, and the list is there when they need it.
 */

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApiError, errorKey } from '@taxi/api-client';

import {
  Banner,
  Button,
  Card,
  CardHeader,
  cx,
  Icon,
  KeyValue,
  KeyValueRow,
  Pill,
  Sub,
} from '../ui';
import { callsApi } from '../calls/api';
import { useDispatch } from '../shell/dispatchContext';
import { useTenantFormats } from '../shell/formats';
import { usePreferences } from '../shell/preferencesContext';
import { usePhone } from '../shell/softphoneContext';
import { useTelephoneContext } from '../shell/telephoneContext';
import { telephoneApi } from '../telephony/api';
import { AssignPanel } from './AssignPanel';
import { ordersApi, type OrderDetail as Detail } from './api';
import { useCancellationReasonNames } from './cancellationReasons';
import { gapLabel, historyReason, isRiderReason } from './history';
import { TakeOver } from './TakeOver';
import { stateTone } from './states';
import { useFeatureName } from '../vehicles/featureNames';
import { className as vehicleClassName, inFormOrder } from '../vehicles/words';

export function OrderDetail({
  order,
  onClose,
  onChanged,
  onCancelled,
  automatic,
  onAutomaticChanged,
  candidateLimit,
  className,
}: {
  order: Detail;
  onClose: () => void;
  onChanged: () => void;
  onCancelled: () => void;
  /**
   * Whether the automation still has this order, when the caller knows.
   *
   * Undefined rather than false where it is not known, and the take-over button is then not
   * drawn at all. The order itself does not carry this — it is dispatch's state, not the
   * order's — so it arrives from whoever is also holding the queue. The timeline, which
   * shows finished rides, passes nothing and wants nothing: there is no automation left to
   * take an order away from.
   */
  automatic?: boolean;
  onAutomaticChanged?: () => void;
  /** The map view shows the two nearest drivers and no more. */
  candidateLimit?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const featureNameOf = useFeatureName();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const live =
    order.state !== 'Completed' && order.state !== 'Cancelled' && order.state !== 'NoShow';

  // The caller this order was taken from, while they are still in the dispatcher's ear:
  // the order remembers its call, and the telephone says which call is mine now. Only
  // then does holding belong here — from the order the dispatcher is reading, so the
  // caller, the order and the driver stay one thing on the screen rather than three.
  const { orderOnCall } = useDispatch();
  const { mine, myCall, onHold } = useTelephoneContext();
  const phone = usePhone();
  const callerOnLine =
    orderOnCall !== null && orderOnCall.orderId === order.id && myCall?.callId === orderOnCall.callId
      ? orderOnCall.callId
      : null;

  // Ringing somebody is offered when the dispatcher has a telephone and is not on it —
  // either of their telephones, which is what `myCall` knows and the browser alone does
  // not — and never to somebody already listening to the firm's hold music.
  const free = mine !== null && myCall === null && phone.call.state === 'idle';
  const canRing = (number: string) => free && !onHold.includes(number);

  async function attempt(action: () => Promise<unknown>, couldNot: string) {
    setBusy(true);
    setFailed(null);

    try {
      await action();
    } catch (failure) {
      setFailed(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), { defaultValue: t(couldNot) })
          : t(couldNot),
      );
    } finally {
      setBusy(false);
    }
  }

  const holdCaller = (callId: string) =>
    attempt(() => telephoneApi.park(callId), 'softphone.couldNotHold');

  // One click. The dispatcher's own telephone rings first, then the number, so nobody
  // sits listening to ringing they did not start — the same shape as the missed-call queue.
  const ring = (number: string) => attempt(() => callsApi.dial(number), 'order.couldNotCall');

  return (
    <Card className={cx('flex flex-col min-h-0', className)}>
      <CardHeader
        className="shrink-0"
        title={
          <>
            <span className="text-ink-3 font-normal">{t('order.title')}</span>
            <span className="font-mono font-semibold tracking-[0.03em]">{order.reference}</span>
            {automatic !== undefined && (
              <TakeOver
                orderId={order.id}
                automatic={automatic}
                onChanged={onAutomaticChanged ?? onChanged}
              />
            )}
            <Pill tone={stateTone(order.state)}>
              {t(`states.${order.state}`, { defaultValue: order.state })}
            </Pill>
          </>
        }
        actions={
          <>
            {live && (
              <Button
                size="sm"
                variant="danger"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  // No confirmation, deliberately: a dispatcher cancels an order while the
                  // caller is still on the line saying they no longer want it.
                  void ordersApi
                    .cancel(order.id, 'dispatcher.cancelled')
                    .then(onCancelled)
                    .finally(() => setBusy(false));
                }}
              >
                {t('order.cancel')}
              </Button>
            )}
            <Button size="sm" variant="quiet" onClick={onClose}>
              {t('order.close')}
            </Button>
          </>
        }
      />

      {failed !== null && <Banner tone="danger">{failed}</Banner>}

      <div className="flex flex-col min-h-0 overflow-y-auto">
        <div className="px-3.5 py-3 flex flex-col gap-3.5">
        <KeyValue>
          <KeyValueRow label={t('order.pickup')}>{order.pickupAddress}</KeyValueRow>

          {order.destinationAddress != null && (
            <KeyValueRow label={t('order.destination')}>{order.destinationAddress}</KeyValueRow>
          )}

          {order.pickupNotes != null && (
            <KeyValueRow label={t('order.notes')}>{order.pickupNotes}</KeyValueRow>
          )}

          {/* What the caller asked the car to be — the reason this order went to some cars
              and not others (#369). */}
          {(order.vehicleCategory != null || (order.vehicleFeatures ?? []).length > 0) && (
            <KeyValueRow label={t('vehicles.featuresLabel')}>
              {[
                ...(order.vehicleCategory != null ? [vehicleClassName(t, order.vehicleCategory)] : []),
                ...inFormOrder(order.vehicleFeatures ?? []).map(featureNameOf),
              ].join(' · ')}
            </KeyValueRow>
          )}

          {order.customerPhone != null && (
            <KeyValueRow label={t('order.customerPhone')}>
              {/* A link, so a dispatcher with a softphone rings back with one keystroke. */}
              <a href={`tel:${order.customerPhone}`} className="text-brand no-underline">
                {order.customerPhone}
              </a>
              {order.customerName != null && <Sub> {order.customerName}</Sub>}
              {/* On the caller's own line rather than in the header: this is the person
                  in the dispatcher's ear, and the header has no room for a third button.
                  While they are, the one thing to do with them is hold; otherwise, ring. */}
              {callerOnLine !== null ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-2"
                  disabled={busy}
                  onClick={() => void holdCaller(callerOnLine)}
                >
                  {t('order.holdCaller')}
                </Button>
              ) : (
                canRing(order.customerPhone) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-2"
                    disabled={busy}
                    onClick={() => void ring(order.customerPhone!)}
                  >
                    {t('order.call')}
                  </Button>
                )
              )}
            </KeyValueRow>
          )}

          {order.driverName != null && (
            <KeyValueRow label={t('order.driver')}>
              {/* The same row for the driver as for the customer, number and button alike,
                  because the two calls a dispatcher makes about an order are to those two. */}
              {order.driverPhone != null ? (
                <a href={`tel:${order.driverPhone}`} className="text-brand no-underline">
                  {order.driverPhone}
                </a>
              ) : (
                <span className="text-ink-3">{t('order.noDriverPhone')}</span>
              )}
              <Sub>
                {' '}
                {order.driverName}
                {order.driverCallSign != null && ` · ${order.driverCallSign}`}
              </Sub>
              {order.driverPhone != null && canRing(order.driverPhone) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-2"
                  disabled={busy}
                  onClick={() => void ring(order.driverPhone!)}
                >
                  {t('order.call')}
                </Button>
              )}
            </KeyValueRow>
          )}

          {order.rating != null && (
            <KeyValueRow label={t('order.rating')}>
              {/* Stars rather than a number, because a dispatcher scanning a list of
                  finished rides is looking for the short ones, and a shape reads faster
                  than a digit. The comment follows because it is the part that says why. */}
              <span aria-label={t('order.ratingStars', { count: order.rating })}>
                {'★'.repeat(order.rating)}
              </span>
              {order.ratingComment != null && <Sub> {order.ratingComment}</Sub>}
            </KeyValueRow>
          )}

          {/* The other direction: the driver's word about the passenger, which is what
              puts "waits outside" or "did not pay" beside the number next time it rings. */}
          {order.driverRating != null && (
            <KeyValueRow label={t('order.driverRating')}>
              <span aria-label={t('order.ratingStars', { count: order.driverRating })}>
                {'★'.repeat(order.driverRating)}
              </span>
              {order.driverRatingComment != null && <Sub> {order.driverRatingComment}</Sub>}
            </KeyValueRow>
          )}

            <Fare order={order} />
          </KeyValue>

          <History order={order} formats={formats} />
        </div>

        {live && (
          <div className="border-t border-rule">
            <AssignPanel
              orderId={order.id}
              currentDriverId={order.driverId}
              onAssigned={onChanged}
              limit={candidateLimit}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * The history, with seconds and with the gap since the previous event.
 *
 * The gap is the column somebody actually reads: an offer and its refusal are two events,
 * so the number between them is exactly how long the driver took to say no.
 */
function History({
  order,
  formats,
}: {
  order: Detail;
  formats: { timeWithSeconds: (iso: string) => string };
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { access } = usePreferences();

  // The firm's list, only for an order a passenger cancelled with a reason.
  const firmReasons = useCancellationReasonNames(
    order.history.some((entry) => isRiderReason(entry.reason)),
  );

  // Once it is over and somebody drove it, its journey can be replayed — by whoever may
  // open the reports, which is where the replay lives.
  const replayable =
    access.reports &&
    ['Completed', 'Cancelled', 'NoShow'].includes(order.state) &&
    order.history.some((entry) => entry.toState === 'Assigned');

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-caps font-semibold tracking-[0.06em] uppercase text-ink-3">
          {t('order.history')}
        </span>
        {replayable && (
          <Button
            size="sm"
            title={t('order.replayHelp')}
            onClick={() => navigate(`/reports/journeys?order=${order.id}`)}
          >
            <Icon name="route" small />
            {t('order.replay')}
          </Button>
        )}
      </div>

      <ol className="grid grid-cols-[58px_44px_minmax(0,1fr)] gap-x-2.5 gap-y-1 text-secondary m-0 p-0 list-none">
        {order.history.map((entry, index) => {
          const why = historyReason(t, entry.reason, firmReasons);

          return (
            <li key={index} className="contents">
              <time dateTime={entry.occurredAt} className="text-ink-3 tabular-nums">
                {formats.timeWithSeconds(entry.occurredAt)}
              </time>
              <span className="text-ink-3 text-right text-[11.5px] tabular-nums">
                {gapLabel(order.history[index - 1]?.occurredAt, entry.occurredAt)}
              </span>
              <span className="min-w-0">
                {t(`states.${entry.toState}`, { defaultValue: entry.toState })}
                <Sub>
                  {' '}
                  {t(`actors.${entry.actor}`, { defaultValue: entry.actor })}
                  {why !== null && ` · ${why}`}
                </Sub>
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * What this order costs, or is expected to.
 *
 * Never the computed figure alone once a metered amount exists — the car's own sealed
 * taximeter is what the passenger paid, and the computed one beside it is only the
 * cross-check. Before either exists, the estimate is shown and said to be one, because a
 * dispatcher taking a call needs an answer to "how much will it be?" too.
 */
function Fare({ order }: { order: Detail }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const paid = order.meteredFareAmount ?? order.finalFareAmount;
  const amount = paid ?? order.estimatedFareAmount;

  // A ride that never happened has no fare and may still have a fee. Charges are read from
  // the fare lines rather than from a total, because they are deliberately not in one: a
  // cancellation fee is not what a journey cost, and folding it into the takings would put
  // money in the shift book that no driver was ever handed.
  const charges = order.fareLines.filter(
    (line) => line.code === 'fare.cancellation' || line.code === 'fare.no_show',
  );

  if ((amount == null || order.fareCurrency == null) && charges.length === 0) return null;

  return (
    <>
      {amount != null && order.fareCurrency != null && (
        <KeyValueRow label={paid != null ? t('order.farePaid') : t('order.fareEstimated')}>
          <span className="tabular-nums">{formats.money(amount)}</span>
        </KeyValueRow>
      )}

      {charges.map((charge) => (
        <Fragment key={charge.code}>
          <KeyValueRow label={t(charge.code)}>
            <span className="tabular-nums">{formats.money(charge.amount)}</span>
          </KeyValueRow>
        </Fragment>
      ))}
    </>
  );
}

/** The one-line banner that says an order was just taken, for a card footer. */
export function TakenBanner({ reference }: { reference: string }) {
  const { t } = useTranslation();

  return (
    <Banner tone="ok" footer>
      {t('order.taken', { reference })}
    </Banner>
  );
}
